import { useTranslation } from 'react-i18next';
import { Banner } from '~/ui/Banner.js';
import { Button } from '~/ui/Button.js';
import { Cover } from '~/ui/Cover.js';

/**
 * The one-time offer to bring a device's games into the account that just
 * signed in.
 *
 * Asked once, after a sign-in and never after a registration. Registering
 * adopts the device's player outright, so there is nothing to move; signing in
 * switches to a player who already exists, and the games played on this device
 * belong to somebody else until their owner says otherwise.
 *
 * **Never merge without asking.** Two people's statistics colliding is the
 * scenario that makes a player angriest, and it cannot be undone. A missed
 * import can: declining leaves the games where they are.
 *
 * The rating line is the part that must be said out loud. Glicko-2 is
 * path-dependent, so two rating histories cannot be added together. If the
 * account has no rating for a mode and format the device's row moves across
 * whole; otherwise the account's stands and the device's is dropped. Saying so
 * before the tap is the difference between a rule and a surprise.
 *
 * Presentational on purpose: the calls live with the screen that fires it, so
 * the workshop can render this state at `/dev/stage` without a server.
 */

export interface ImportPromptProps {
  /** How many games the device's own player has played. */
  readonly gamesCount: number;
  /** True while a choice is in flight, so neither button can be pressed twice. */
  readonly busy?: boolean;
  /** A refusal from the server, already turned into a sentence. */
  readonly error?: string | null;
  readonly onAccept: () => void;
  readonly onDecline: () => void;
}

export function ImportPrompt({
  gamesCount,
  busy = false,
  error = null,
  onAccept,
  onDecline,
}: ImportPromptProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    // Escape declines rather than doing nothing: the prompt has a safe way out,
    // and leaving the games where they are is it.
    <Cover aria-labelledby="import-title" onEscape={onDecline}>
      <h2 id="import-title">{t('import.title')}</h2>
      <p className="text-muted">{t('import.body', { count: gamesCount })}</p>
      <p>{t('import.offer')}</p>
      <p className="text-muted">{t('import.rating')}</p>
      {error !== null && <Banner role="alert">{error}</Banner>}
      <div className="row flex flex-wrap items-center gap-3">
        <Button variant="primary" disabled={busy} onClick={onAccept}>
          {busy ? t('common.working') : t('import.accept')}
        </Button>
        <Button disabled={busy} onClick={onDecline}>
          {t('import.decline')}
        </Button>
      </div>
    </Cover>
  );
}
